import { Skeleton } from "@resume-studio/ui";
import * as React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";

import { toast } from "@/admin/hooks/use-toast";
import {
  AdminCampaignDetail,
  CreateCampaignInput,
  useAdminCampaign,
  useUpdateAdminCampaign,
} from "@/admin/services";

import { PageHeader } from "../../../_components/page-header";
import {
  CampaignForm,
  CampaignFormState,
  initialCampaignFormState,
} from "../../_components/campaign-form";

const isoToDateInput = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const detailToFormState = (detail: AdminCampaignDetail): CampaignFormState => ({
  slug: detail.slug,
  name: detail.name,
  description: detail.description,
  offerType: detail.offerType,
  grantedPlan: detail.grantedPlan,
  grantedDurationDays: detail.grantedDurationDays,
  startsAt: isoToDateInput(detail.startsAt),
  endsAt: isoToDateInput(detail.endsAt),
  targetAudience: detail.targetAudience,
  maxRedemptions: detail.maxRedemptions,
  bannerText: detail.bannerText ?? "",
  pageHeadline: detail.pageHeadline ?? "",
  pageSubheadline: detail.pageSubheadline ?? "",
  discountPercent: detail.discountPercent ?? initialCampaignFormState.discountPercent,
  discountDurationMonths:
    detail.discountDurationMonths ?? initialCampaignFormState.discountDurationMonths,
  stripeCouponId: detail.stripeCouponId ?? "",
  // Edit mode reuses this slot for "swap coupon" — always rendered, never a toggle.
  enableStripeCouponOverride: true,
});

export const AdminEditCampaignPage = () => {
  const params = useParams<{ id: string }>();
  const id = params.id ?? "";
  const navigate = useNavigate();
  const { data, isLoading } = useAdminCampaign(id);
  const update = useUpdateAdminCampaign(id);

  const [form, setForm] = React.useState<CampaignFormState | null>(null);

  // Hydrate the form once the campaign loads. We keep local state separate from
  // the query cache so admin edits don't bleed back into the cached row before
  // the PATCH actually succeeds.
  React.useEffect(() => {
    if (!data || form !== null) return;
    setForm(detailToFormState(data));
  }, [data, form]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form || !data) return;

    if (!form.name) {
      toast({ variant: "error", title: "Name is required." });
      return;
    }
    if (new Date(form.endsAt) <= new Date(form.startsAt)) {
      toast({ variant: "error", title: "End date must be after start date." });
      return;
    }

    // Build an UPDATE payload — slug, offerType, granted plan, and discount
    // shape are immutable post-create (server enforces too). We diff against
    // the loaded campaign so we only send changed fields, keeping audit log
    // entries readable.
    const payload: Partial<CreateCampaignInput> = {};
    if (form.name !== data.name) payload.name = form.name;
    const description = form.description || form.name;
    if (description !== data.description) payload.description = description;

    const startsIso = new Date(form.startsAt).toISOString();
    if (startsIso !== data.startsAt) payload.startsAt = startsIso;
    const endsIso = new Date(form.endsAt).toISOString();
    if (endsIso !== data.endsAt) payload.endsAt = endsIso;

    if (form.targetAudience !== data.targetAudience) payload.targetAudience = form.targetAudience;
    if ((form.maxRedemptions ?? null) !== (data.maxRedemptions ?? null)) {
      payload.maxRedemptions = form.maxRedemptions || null;
    }

    const banner = form.bannerText || null;
    if (banner !== data.bannerText) payload.bannerText = banner;
    const headline = form.pageHeadline || null;
    if (headline !== data.pageHeadline) payload.pageHeadline = headline;
    const sub = form.pageSubheadline || null;
    if (sub !== data.pageSubheadline) payload.pageSubheadline = sub;

    if (form.offerType === "FREE_ACCESS" && form.grantedDurationDays !== data.grantedDurationDays) {
      payload.grantedDurationDays = form.grantedDurationDays;
    }

    // Only PATCH the coupon when it actually changed. Sending the existing
    // value back wouldn't break anything but it would clutter the audit log
    // with no-op `stripeCouponId` diffs.
    if (
      form.offerType === "STRIPE_DISCOUNT" &&
      form.stripeCouponId.trim() &&
      form.stripeCouponId !== data.stripeCouponId
    ) {
      payload.stripeCouponId = form.stripeCouponId.trim();
    }

    if (Object.keys(payload).length === 0) {
      toast({ title: "No changes to save." });
      navigate(`/campaigns/${id}`);
      return;
    }

    try {
      await update.mutateAsync(payload);
      toast({ title: `Saved “${form.name}”.` });
      navigate(`/campaigns/${id}`);
    } catch {
      // Axios interceptor already toasts the server message.
    }
  };

  if (isLoading || !data || !form) {
    return (
      <>
        <Helmet>
          <title>Edit campaign · Admin</title>
        </Helmet>
        <PageHeader title="Loading…" trail={["Admin", "Campaigns"]} />
        <Skeleton className="h-64 w-full" />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Edit {data.name} · Admin</title>
      </Helmet>

      <PageHeader
        title={`Edit ${data.name}`}
        trail={["Admin", "Campaigns", data.name]}
        description="Slug, offer type, and discount shape are locked after creation."
      />

      <CampaignForm
        value={form}
        onChange={setForm as React.Dispatch<React.SetStateAction<CampaignFormState>>}
        onSubmit={submit}
        onCancel={() => navigate(`/campaigns/${id}`)}
        mode="edit"
        submitLabel="Save changes"
        isSubmitting={update.isPending}
      />
    </>
  );
};

export default AdminEditCampaignPage;
